/**
 * Token-Kit Developer Portal — Developer Token Service
 *
 * API calls for managing a developer's own test tokens.
 * These tokens let developers test LLM integration end-to-end
 * without needing a real ai-tokens.me user account.
 */

import { api } from './api.client';

// ── Types ─────────────────────────────────────────────────────────────────

export interface TokenPackage {
  id: string;
  name: string;
  tokens: number;
  priceUsd: number;
  costPerToken: number;
  savings: number;
  popular?: boolean;
}

export interface DeveloperTokenBalance {
  available: number;
  lifetimePurchased: number;
  lifetimeUsed: number;
  developerToken: string;
  tokenPrefix: string;
  hasToken: boolean;
  lastUpdated: string;
}

export interface DeveloperTokenTransaction {
  id: string;
  type: 'purchase' | 'usage' | 'refund';
  status: 'completed' | 'pending' | 'failed';
  tokens: number;
  amountUsd?: number;
  description: string;
  model?: string;
  createdAt: string;
}

export interface ClaimResult {
  transactionId: string;
  tokensAdded: number;
  newBalance: number;
  developerToken: string;
}

// ── API Calls ─────────────────────────────────────────────────────────────

export async function getDeveloperTokenBalance(): Promise<DeveloperTokenBalance> {
  return api.get<DeveloperTokenBalance>('/portal/me/developer-token');
}

export async function getDeveloperTokenPackages(): Promise<TokenPackage[]> {
  return api.get<TokenPackage[]>('/portal/me/developer-token/packages');
}

export async function claimDeveloperTokens(packageId: string): Promise<ClaimResult> {
  return api.post<ClaimResult>('/portal/me/developer-token/claim', { packageId });
}

export async function getDeveloperTokenTransactions(
  limit: number = 50
): Promise<DeveloperTokenTransaction[]> {
  return api.get<DeveloperTokenTransaction[]>('/portal/me/developer-token/transactions', {
    limit: String(limit),
  });
}
